"use client";

import React, { useEffect } from "react";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Plane, FileText, Globe2, ShieldCheck } from "lucide-react";

/**
 * Market-test: compact REAL business/airport reassurance folded into the
 * arrival flow (market-test-plan.md §3 `proof_premium_compact_v1`, research/07
 * CF5). RECONCILED 2026-06-15: the previous version showed a fabricated "4.8/5"
 * aggregate rating + star bars + invented named testimonials. The brand's own
 * research (research/02 niche "things to NOT copy", research/07 CF5,
 * market-test §8, keyword-research compliance) BANS fabricated ratings / review
 * counts / user totals and AggregateRating. So this is now CLAIM-SAFE benefit
 * reassurance for the P2 business / frequent-flyer audience (CF4) — no numbers,
 * no names, no stars. No glass, no gradients.
 */
const EXPERIMENT_ID = "proof_premium_compact_v1";

export default function ReviewsStrip() {
  const { t } = useTranslations();

  // Exposure event (consent-gated, best-effort; `track` dedupes by event_id).
  useEffect(() => {
    try {
      track({
        event_name: "proof_section_viewed",
        event_id: `proof_section_viewed:${EXPERIMENT_ID}`,
        experiment_id: EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const reasons = [
    {
      icon: Plane,
      title: t("sky.reasons.r1.title", "Ready before arrival"),
      body: t("sky.reasons.r1.body", "Install on Wi-Fi before you board and switch on data the moment you land — no SIM line, no airport Wi-Fi needed."),
    },
    {
      icon: FileText,
      title: t("sky.reasons.r2.title", "A receipt for expenses"),
      body: t("sky.reasons.r2.body", "Your order confirmation is emailed instantly — an easy receipt to file for work-trip expenses."),
    },
    {
      icon: Globe2,
      title: t("sky.reasons.r3.title", "Coverage for the whole route"),
      body: t("sky.reasons.r3.body", "Country, regional and global plans on reliable local 4G/5G — pick one plan for a multi-leg itinerary."),
    },
  ];

  return (
    <section className="container mx-auto px-4 py-12">
      <Reveal as="div" y={16} className="text-center max-w-2xl mx-auto mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
          {t("sky.reasons.title", "Built for business and frequent flyers")}
        </h2>
        <p className="text-muted-foreground mt-2">
          {t("sky.reasons.sub", "Convenience, reliability and clean receipts — not the cheapest, the smoothest arrival.")}
        </p>
      </Reveal>

      <Stagger as="div" staggerChildren={0.08} className="grid md:grid-cols-3 gap-4">
        {reasons.map(({ icon: Icon, title, body }) => (
          <StaggerItem
            key={title}
            as="div"
            y={14}
            className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md hover:border-primary/40 flex flex-col"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary mb-4">
              <Icon className="w-5 h-5" />
            </span>
            <h3 className="font-semibold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1.5 flex-1">{body}</p>
          </StaggerItem>
        ))}
      </Stagger>

      {/* Claim-safe trust bar (no fabricated rating/count) */}
      <Reveal as="div" y={12} className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-sm flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-2 text-center text-sm font-medium text-foreground/80">
        <span className="inline-flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-success" />
          {t("sky.reasons.bar1", "Secure encrypted checkout")}
        </span>
        <span className="inline-flex items-center gap-2">
          <Plane className="w-4 h-4 text-primary" />
          {t("sky.reasons.bar2", "Works the moment you land")}
        </span>
        <span className="inline-flex items-center gap-2">
          <FileText className="w-4 h-4 text-primary" />
          {t("sky.reasons.bar3", "Instant emailed receipt")}
        </span>
      </Reveal>
    </section>
  );
}
